<template>
  <md-drawer md-right md-fixed :md-active.sync="isVisible" class="request-drawer">
    <div class="drawer-head">
      <div>
        <h2>Request report</h2>
        <p>We'll generate this in the background and email you a download link when it's ready.</p>
      </div>
      <button class="drawer-close" type="button" @click="close()" aria-label="Close">
        <md-icon>close</md-icon>
      </button>
    </div>

    <div class="drawer-body">
      <div class="field">
        <label>Report type</label>
        <md-radio v-model="reportType" value="transaction">Transaction</md-radio>
        <md-radio v-model="reportType" value="wallet">Wallet</md-radio>
      </div>

      <div class="field">
        <label>Period</label>
        <p class="hint">A report can cover any range of up to one month.</p>
        <md-radio v-model="period" value="today">Today</md-radio>
        <md-radio v-model="period" value="yesterday">Yesterday</md-radio>
        <md-radio v-model="period" value="custom">Custom range</md-radio>

        <template v-if="period === 'custom'">
          <div class="range">
            <md-datepicker
              md-immediately
              v-model="startDate"
              :md-disabled-dates="disabledStartDates"
            >
              <label>From</label>
            </md-datepicker>
            <md-datepicker
              md-immediately
              v-model="endDate"
              :md-disabled-dates="disabledEndDates"
            >
              <label>To</label>
            </md-datepicker>
          </div>
          <p class="error" v-if="rangeError">{{ rangeError }}</p>
          <p class="hint" v-else-if="latestEndLabel">
            Latest end date for this start: {{ latestEndLabel }}.
          </p>
        </template>
      </div>

    </div>

    <div class="drawer-foot">
      <app-button variant="ghost" @click="close()">Cancel</app-button>
      <app-button :disabled="submitting || !!rangeError" @click="submit()">
        {{ submitting ? 'Requesting…' : 'Request report' }}
      </app-button>
    </div>
  </md-drawer>
</template>

<script>
import {
  format, subDays, addMonths, startOfDay, endOfDay, isAfter, isValid,
} from 'date-fns';
import http from '../../utils/Http.util';
import AppButton from '../../components/ui/AppButton.vue';

const REPORT_CHANNEL = 'apix';

const MAX_RANGE_MONTHS = 1;

// The last day a range starting on `start` may include. A calendar month
// rather than a fixed day count, so 1 Jan - 31 Jan and 1 Feb - 28 Feb are
// both a full month's worth of data.
const maxEndDate = (start) => subDays(addMonths(startOfDay(start), MAX_RANGE_MONTHS), 1);

// The datepickers hand back a Date, but a hand-typed value can arrive as a
// string or as an Invalid Date, so normalise before doing any date maths.
const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isValid(date) ? startOfDay(date) : null;
};

export default {
  name: 'request-report-modal',
  components: {
    AppButton,
  },
  data() {
    return {
      isVisible: false,
      submitting: false,
      reportType: 'transaction',
      period: 'today',
      startDate: new Date(),
      endDate: new Date(),
    };
  },
  computed: {
    // Recomputed (rather than plain methods passed as props) so the calendars
    // re-evaluate which days are selectable whenever the start date moves.
    disabledStartDates() {
      const today = startOfDay(new Date());
      return (date) => isAfter(startOfDay(date), today);
    },
    disabledEndDates() {
      const today = startOfDay(new Date());
      const start = toDate(this.startDate);
      const latest = start ? maxEndDate(start) : null;
      return (date) => {
        const day = startOfDay(date);
        if (isAfter(day, today)) return true;
        if (!start) return false;
        return isAfter(start, day) || isAfter(day, latest);
      };
    },
    latestEndLabel() {
      const start = toDate(this.startDate);
      if (!start) return null;
      const latest = maxEndDate(start);
      const today = startOfDay(new Date());
      return format(isAfter(latest, today) ? today : latest, 'D MMM YYYY');
    },
    rangeError() {
      if (this.period !== 'custom') return null;
      const start = toDate(this.startDate);
      const end = toDate(this.endDate);
      if (!start || !end) return 'Pick both a start and an end date.';
      if (isAfter(start, end)) return 'The start date cannot be after the end date.';
      if (isAfter(end, startOfDay(new Date()))) return 'The end date cannot be in the future.';
      if (isAfter(end, maxEndDate(start))) {
        const latest = format(maxEndDate(start), 'D MMM YYYY');
        return `Ranges are capped at one month, so this one has to end on or before ${latest}.`;
      }
      return null;
    },
  },
  methods: {
    open() {
      this.isVisible = true;
      this.submitting = false;
      this.reportType = 'transaction';
      this.period = 'today';
      this.startDate = new Date();
      this.endDate = new Date();
    },
    close() {
      this.isVisible = false;
    },
    resolvedRange() {
      const now = new Date();
      if (this.period === 'yesterday') {
        const day = subDays(now, 1);
        return { start: day, end: day };
      }
      if (this.period === 'custom') {
        return { start: toDate(this.startDate) || now, end: toDate(this.endDate) || now };
      }
      return { start: now, end: now };
    },
    async submit() {
      if (this.rangeError) return;
      this.submitting = true;
      const { start, end } = this.resolvedRange();
      const body = {
        channel: REPORT_CHANNEL,
        report_type: this.reportType,
        start_date: format(startOfDay(start), 'YYYY-MM-DD HH:mm:ss'),
        end_date: format(endOfDay(end), 'YYYY-MM-DD HH:mm:ss'),
      };

      try {
        const { data } = await http.authReq('/dashboard/reports', body, 'post');
        this.isVisible = false;
        this.$emit('created', data);
      } catch (err) {
        this.$emit('error', err.message || 'Something went wrong. Please try again.');
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.request-drawer.md-drawer {
  width: 420px;
  display: flex;
  flex-direction: column;
}

.drawer-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 16px;
  border-bottom: 1px solid var(--border);
}
.drawer-head h2 {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 700;
}
.drawer-head p {
  margin: 0;
  font-size: 12.5px;
  color: var(--muted);
  max-width: 30ch;
}
.drawer-close {
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--faint);
  border-radius: var(--radius);
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}
.drawer-close:hover { background: var(--surface-2); color: var(--ink); }

.drawer-body {
  padding: 20px 22px;
  flex: 1;
  overflow-y: auto;
}
.drawer-foot {
  padding: 16px 22px;
  border-top: 1px solid var(--border);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.field {
  margin-bottom: 18px;
}
.field > label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--faint);
  margin-bottom: 8px;
}
.field .hint {
  font-size: 12px;
  color: var(--faint);
  margin: 0 0 8px;
}
.field .error {
  font-size: 12px;
  color: var(--critical);
  margin: 4px 0 0;
}

.range {
  display: flex;
  gap: 14px;
}
.range .md-datepicker {
  flex: 1;
  min-width: 0;
}
</style>
